// pages/BusinessCardListPage.tsx

import React, { useState, useEffect, useRef, useCallback } from 'react';
import dayjs from 'dayjs';
import { Layout, Typography, Button, List, Avatar, Tag, Space, Select, Collapse, Input, Form, Row, Col, DatePicker, message, Spin, Switch } from 'antd';
import { ArrowLeftOutlined, UserOutlined, BankOutlined, StarOutlined, DownOutlined, UpOutlined, MailOutlined, HomeOutlined, FileTextOutlined, SearchOutlined, FilterOutlined, LoadingOutlined, PictureOutlined, EyeOutlined, EyeInvisibleOutlined, CloseCircleFilled } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { getRoutePath } from '../config';
import { businessCardAPI, saveCurrentDetailCustomerId } from '../services/api';
import { Customer, CustomerSearchRequest, CustomerImageMap, CustomerImageEntry } from '../types/businessCard';
import { generateRankColors, getRankColorSync, saveCustomerRank, getRankColorByIndex } from '../services/rank-utils';
import { useRankColors } from '../hooks/useRankColors';
import { useRankOptions } from '../hooks/useRankOptions';
import { useContactUsers } from '../hooks/useContactUsers';
import { optionFieldAPI, OptionFieldListResponse, convertOptionFieldValuesToApiFormat } from '../services/option-field-api';
import OptionFieldGroup from '../components/OptionFields/OptionFieldGroup';
import './BusinessCardListPage.css';
import { isInFlutter as isInFlutterUtil } from '../utils/flutterDetection';
import { useAuth } from '../contexts/AuthContext';
import { hasAuth, AUTH } from '../utils/auth';
import {
  SEARCH_FORM_VALUES_KEY,
  SEARCH_PARAMS_KEY,
  SELECTED_CONTACT_USERS_KEY,
  SCROLL_POSITION_KEY,
  LOADED_ITEMS_COUNT_KEY,
  ADVANCED_SEARCH_EXPANDED_KEY,
  SEARCH_EXPANDED_KEY,
} from '../constants/storageKeys';

const { Header, Content } = Layout;
const { Title, Text } = Typography;

// ソート定義
const SORT_OPTIONS = [
  { value: 'lastviewdate',           label: '閲覧日'     },
  { value: 'businesscardcreatetime', label: '名刺登録日' },
  { value: 'companyname',            label: '会社名'     },
] as const;

type SortItemValue = typeof SORT_OPTIONS[number]['value'];

const BusinessCardListPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  // 名刺取込（カメラ起動）ボタンの表示可否（ロール権限）
  const canRegisterCustomer = hasAuth(user, AUTH.SC_CUSTOMER_EDIT);

  // Flutter環境の検出
  const isInFlutter = isInFlutterUtil();
  const [pageSize, setPageSize] = useState<number>(20);
  const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set());
  const [searchExpanded, setSearchExpanded] = useState<boolean>(() => {
    // 詳細画面からの戻りの場合のみ、保存された状態を復元
    const savedScrollPosition = localStorage.getItem(SCROLL_POSITION_KEY);
    if (savedScrollPosition) {
      const savedState = localStorage.getItem(SEARCH_EXPANDED_KEY);
      return savedState === 'true';
    }
    return false;
  });
  const [advancedSearchExpanded, setAdvancedSearchExpanded] = useState<boolean>(() => {
    // 詳細画面からの戻りの場合のみ、保存された状態を復元
    const savedScrollPosition = localStorage.getItem(SCROLL_POSITION_KEY);
    if (savedScrollPosition) {
      const savedState = localStorage.getItem(ADVANCED_SEARCH_EXPANDED_KEY);
      return savedState === 'true';
    }
    return false;
  });
  const [form] = Form.useForm();
  
  // API取得用の状態
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  
  // 動的にランクの色を管理するための状態
  const [rankColors, setRankColors] = useRankColors();
  
  // ランクオプションの状態
  const { rankOptions, isLoading: isLoadingRanks } = useRankOptions();
  
  // 担当者関連の状態
  const { contactUsers, isLoading: isLoadingContactUsers } = useContactUsers();
  const [selectedContactUsers, setSelectedContactUsers] = useState<number[]>([]);

  // 拡張フィールド関連の状態
  const [optionFields, setOptionFields] = useState<OptionFieldListResponse | null>(null);
  const [optionFieldValues, setOptionFieldValues] = useState<Record<string, any>>({});
  const [loadingOptionFields, setLoadingOptionFields] = useState<boolean>(false);
  const [rangeFromValues, setRangeFromValues] = useState<Record<string, any>>({});
  const [rangeToValues, setRangeToValues] = useState<Record<string, any>>({});

  // ソートの状態
  const [sortItem,  setSortItem]  = useState<SortItemValue>('lastviewdate');
  const [sortOrder, setSortOrder] = useState<boolean>(false);

  
  // 検索パラメータ
  const [searchParams, setSearchParams] = useState<CustomerSearchRequest>({
    limit: pageSize,
    offset: 0,
    usecustomernamefurigana: true,
    usecompanynamefurigana: true
  });

  // 検索パラメータのベース構築（共通プロパティをまとめる）
  const buildSearchParams = useCallback((
    overrides: Partial<CustomerSearchRequest> = {}
  ): CustomerSearchRequest => ({
    ...searchParams,
    limit: pageSize,
    offset: 0,
    usecustomernamefurigana: true,
    usecompanynamefurigana: true,
    sortItem,
    sortOrder,
    ...overrides,
  }), [searchParams, pageSize, sortItem, sortOrder]);

  // 無限スクロール用の状態
  const [hasMore, setHasMore] = useState<boolean>(true);
  const listRef = useRef<HTMLDivElement>(null);

  // 初回マウント判定用のref（検索欄の状態保存を初回スキップするため）
  const isInitialMount = useRef<boolean>(true);

  // 名刺画像表示の状態（ローカルストレージから初期値を取得）
  const [showCardImages, setShowCardImages] = useState<boolean>(() => {
    // ローカルストレージから設定を取得
    const savedSetting = localStorage.getItem('showCardImages');
    // 保存されている場合はその値を使用、なければデフォルトでfalse
    return savedSetting ? savedSetting === 'true' : false;
  });
  
  // 画像読み込み状態の管理
  const [loadingImages, setLoadingImages] = useState<boolean>(false);
  const [customerImages, setCustomerImages] = useState<CustomerImageMap>({}); // 顧客IDをキーとした画像マップ

  // Flutterフッター高さの管理
  const [flutterFooterHeight, setFlutterFooterHeight] = useState<number>(0);

  // 共通関数を使ってランク色を割り当てる
  const assignRankColors = (customerList: Customer[]) => {
    
    // 顧客リストからユニークなランク名を抽出してログ出力
    const uniqueRanks = Array.from(new Set(
      customerList
        .map(customer => customer.rankname)
        .filter(rankname => rankname) // undefined/nullを除外
    ));
    
    // 共通ユーティリティ関数を使用してランク色マッピングを生成
    const newRankColors = generateRankColors(customerList);
    
    // 新しいランク色のマッピングを設定
    setRankColors(newRankColors);
  };

  // 顧客データを取得する関数
  const fetchCustomers = async (params: CustomerSearchRequest, append: boolean = false) => {
    setIsLoading(true);
    try {
      const response = await businessCardAPI.searchCustomers(params);

      if (response.statuscode === 200) {
        // デバッグ用：顧客リストの最初の要素の内容を出力
        if (response.customerlist && response.customerlist.length > 0) {

          // 名刺画像の形式を確認
          const frontImage = response.customerlist[0].businesscardfrontimage;
          if (frontImage) {
          }
        }
        
        const newCustomerList = response.customerlist.map((customer: Customer) => ({
          ...customer,
          // APIレスポンスをビュー用に変換
          position: '' // APIには役職がないので空の文字列を設定
        }));
        
        // デバッグ用：マッピング後の顧客データをサンプル出力
        if (newCustomerList.length > 0) {
        }
        
        // レイジーロードの場合は既存のデータに追加
        if (append) {
          setCustomers(prev => [...prev, ...newCustomerList]);
        } else {
          // 通常の検索の場合は置き換え
          setCustomers(newCustomerList);
        }
        
        // 総件数を設定
        setTotalCount(response.customerlistsize);
        
        // 続きがあるかどうかを設定
        setHasMore(params.offset + newCustomerList.length < response.customerlistsize);
        
        // ランクの色を動的に割り当て
        assignRankColors(append ? [...customers, ...newCustomerList] : newCustomerList);
        
        // 画像表示がONの場合、すべてのデータ取得後に画像を読み込む
        // React の状態更新は非同期なので、直接 newCustomerList を使用して処理
        if (showCardImages && !loadingImages && newCustomerList.length > 0) {
          
          // 十分な遅延を入れて状態更新が完了するのを待つ
          setTimeout(() => {
            fetchCustomerImages(newCustomerList);
          }, 1000);
        }
      } else {
        message.error(response.message || 'データの取得に失敗しました');
        // エラー時は空配列を設定（追加モードでない場合のみ）
        if (!append) {
          setCustomers([]);
          setTotalCount(0);
        }
        setHasMore(false);
      }
    } catch (error) {
      console.error('顧客データ取得エラー:', error);
      message.error('データの取得中にエラーが発生しました');
      // エラー時は空配列を設定（追加モードでない場合のみ）
      if (!append) {
        setCustomers([]);
        setTotalCount(0);
      }
      setHasMore(false);
    } finally {
      setIsLoading(false);
    }
  };
  
  // 追加データを読み込む関数
  const loadMoreData = useCallback(() => {
    if (isLoading || !hasMore) return;
    
    // 次のデータを取得するためのパラメータを設定
    const nextParams = {
      ...searchParams,
      offset: customers.length
    };
    
    setSearchParams(nextParams);
    fetchCustomers(nextParams, true);
  }, [isLoading, hasMore, searchParams, customers.length]);
  
  // 表示/非表示の切り替え時の処理
  useEffect(() => {

    // 設定をローカルストレージに保存
    localStorage.setItem('showCardImages', String(showCardImages));

    // 画像表示がONになり、まだ画像データが少ない場合は画像を取得
    if (showCardImages && Object.keys(customerImages).length < customers.length) {
      fetchCustomerImages(customers);
    }
  }, [showCardImages]);

  // 検索の開閉状態を保存（初回マウント時はスキップ）
  useEffect(() => {
    if (isInitialMount.current) {
      return;
    }
    localStorage.setItem(SEARCH_EXPANDED_KEY, String(searchExpanded));
  }, [searchExpanded]);

  // 詳細検索の開閉状態を保存（初回マウント時はスキップ）
  useEffect(() => {
    if (isInitialMount.current) {
      return;
    }
    localStorage.setItem(ADVANCED_SEARCH_EXPANDED_KEY, String(advancedSearchExpanded));
  }, [advancedSearchExpanded]);

  // 顧客詳細から画像データを取得する関数
  const fetchCustomerImages = async (customersToProcess?: Customer[]) => {
    if (loadingImages) return;
    
    // 処理対象の顧客リスト（指定がなければ現在のcustomers状態を使用）
    const targetCustomers = customersToProcess || customers;
    
    if (targetCustomers.length === 0) {
      return;
    }
    
    setLoadingImages(true);
    
    try {
      // 画像データが未取得の顧客のみを対象にする
      const customersToFetch = targetCustomers.filter(customer => {
        const customerNo = customer.customerno || customer.no;
        return !customerImages[customerNo];
      });
      
      
      if (customersToFetch.length === 0) {
        setLoadingImages(false);
        return;
      }
      
      
      // 最大5件ずつバッチ処理
      const batchSize = 5;
      const batches = Math.ceil(customersToFetch.length / batchSize);
      
      let newImages: CustomerImageMap = { ...customerImages };
      
      for (let i = 0; i < batches; i++) {
        const start = i * batchSize;
        const end = Math.min(start + batchSize, customersToFetch.length);
        const batch = customersToFetch.slice(start, end);
        
        
        // 並列処理で各顧客の詳細を取得
        const detailPromises = batch.map(customer => {
          const customerNo = customer.customerno || customer.no;
          // 閲覧日を更新しないため第2引数なし
          return businessCardAPI.getCustomerDetail(customerNo)
            .then(response => {
              if (response.statuscode === 200 && response.customerdata) {
                // 画像データを抽出
                return {
                  customerNo,
                  images: {
                    front: response.customerdata.businesscardfrontimage,
                    back: response.customerdata.businesscardbackimage,
                    createtime: response.customerdata.businesscardcreatetime
                  }
                };
              }
              return null;
            })
            .catch(error => {
              console.error(`顧客 ${customerNo} の画像取得エラー:`, error);
              return null;
            });
        });
        
        // すべてのリクエストが完了するのを待つ
        const results = await Promise.all(detailPromises);
        
        // 取得した画像データを保存
        results.forEach(result => {
          if (result) {
            newImages[result.customerNo] = {
              front: result.images.front,
              back: result.images.back,
              createtime: result.images.createtime
            };
          }
        });
        
        // 処理が中断された場合（例：コンポーネントがアンマウントされた）
        if (!showCardImages) {
          break;
        }
      }
      
      // 新しい画像データをステートに設定
      setCustomerImages(newImages);
      
    } catch (error) {
      console.error('顧客画像データの取得中にエラーが発生しました:', error);
    } finally {
      setLoadingImages(false);
    }
  };
  
  // 表示件数変更時の処理（初回マウント時はスキップ）
  useEffect(() => {
    if (isInitialMount.current) {
      return;
    }
    const newParams = buildSearchParams();

    setSearchParams(newParams);
    fetchCustomers(newParams);
    setCurrentPage(1);
  }, [pageSize]);
  
  // 拡張フィールド一覧を取得する関数
  const fetchOptionFields = useCallback(async () => {
    try {
      setLoadingOptionFields(true);
      const response = await optionFieldAPI.getOptionFieldList();
      
      if (response.statuscode === 200) {
        setOptionFields(response);
        console.log('拡張フィールド取得成功:', response);
      } else {
        console.error('拡張フィールド取得エラー:', response.message);
      }
    } catch (error) {
      console.error('拡張フィールド取得API呼び出しエラー:', error);
    } finally {
      setLoadingOptionFields(false);
    }
  }, []);

  // 拡張フィールドの値変更ハンドラー
  const handleOptionFieldChange = (fieldKey: string, value: any) => {
    setOptionFieldValues(prev => {
      const newValues = {
        ...prev,
        [fieldKey]: value
      };
      return newValues;
    });
  };

  // 拡張フィールドの範囲検索（開始値）変更ハンドラー
  const handleRangeFromChange = (fieldKey: string, value: any) => {
    setRangeFromValues(prev => ({ ...prev, [fieldKey]: value }));
  };

  // 拡張フィールドの範囲検索（終了値）変更ハンドラー
  const handleRangeToChange = (fieldKey: string, value: any) => {
    setRangeToValues(prev => ({ ...prev, [fieldKey]: value }));
  };

  // optionCustomer（JSON文字列）からフィールド値を復元
  const restoreOptionFieldValuesFromOptionCustomer = (optionCustomerJson: string) => {
    const values: Record<string, any> = {};
    const fromValues: Record<string, any> = {};
    const toValues: Record<string, any> = {};
    
    try {
      const optionCustomer = JSON.parse(optionCustomerJson);
      
      Object.entries(optionCustomer).forEach(([fieldKey, fieldData]: [string, any]) => {
        if (!fieldData || typeof fieldData !== 'object') {
          return;
        }
        
        const { type, value, issearchrangeselect } = fieldData;
        
        // 範囲検索
        if (issearchrangeselect === true && Array.isArray(value) && value.length === 2) {
          if (value[0]) {
            fromValues[fieldKey] = value[0];
          }
          if (value[1]) {
            toValues[fieldKey] = value[1];
          }
        } else {
          // 通常フィールド
          values[fieldKey] = value;
        }
      });
    } catch (error) {
      console.error('optionCustomer の解析に失敗:', error);
    }
    
    return { values, fromValues, toValues };
  };

  // 担当者選択のレンダリング関数
  const renderContactUsersSelector = () => {
    return (
      <div>
        {/* 選択済み担当者のタグ表示 */}
        <div style={{ marginBottom: selectedContactUsers.length > 0 ? '8px' : 0 }}>
          {selectedContactUsers.map((userno) => {
            const user = contactUsers.find(u => u.userno === userno);
            return (
              <Tag 
                key={userno}
                style={{ 
                  padding: '4px 8px', 
                  marginBottom: '6px', 
                  fontSize: '14px',
                  display: 'inline-flex',
                  alignItems: 'center'
                }}
                closable={true}
                onClose={(e) => {
                  e.preventDefault();
                  // 担当者を削除
                  setSelectedContactUsers(prev => prev.filter(id => id !== userno));
                }}
              >
                {user ? user.username : (userno < 0 ? 'システム管理者' : `担当者(ID:${userno})`)}
              </Tag>
            );
          })}
        </div>
        
        {/* 担当者追加用のドロップダウン */}
        <Select
          style={{ width: '100%', marginTop: '8px' }}
          placeholder="+ 担当者を追加"
          onChange={(value) => {
            if (value && !selectedContactUsers.includes(Number(value))) {
              // 選択された担当者を追加
              setSelectedContactUsers(prev => [...prev, Number(value)]);
            }
          }}
          value={null} // 常に未選択状態を維持
          loading={isLoadingContactUsers}
        >
          {contactUsers
            .filter(user => !selectedContactUsers.includes(user.userno))
            .map(user => (
              <Select.Option key={user.userno} value={user.userno}>
                {user.username} ({user.deptname || '-'})
              </Select.Option>
            ))
          }
        </Select>
      </div>
    );
  };
  
  // 検索条件を保存する関数 - ローカルストレージを使用
  const saveSearchConditions = (params = searchParams) => {
    try {
      // 1. フォーム値を取得して保存
      const formValues = form.getFieldsValue();
      localStorage.setItem(SEARCH_FORM_VALUES_KEY, JSON.stringify(formValues));
      
      // 2. 検索パラメータを保存（現在のoffsetを考慮）
      // 現在の表示中のデータ数をoffsetとして使用
      const paramsWithCurrentOffset = {
        ...params,
        offset: customers.length > 0 ? customers.length - (customers.length % params.limit) : 0
      };
      
      localStorage.setItem(SEARCH_PARAMS_KEY, JSON.stringify(paramsWithCurrentOffset));
      
      // 3. 選択された担当者を保存
      localStorage.setItem(SELECTED_CONTACT_USERS_KEY, JSON.stringify(selectedContactUsers));
      
    } catch (error) {
      console.error('検索条件の保存に失敗:', error);
    }
  };
  
  // 保存された検索条件を復元する関数 - ローカルストレージから
  const restoreSearchConditions = () => {
    try {
      let hasRestoredConditions = false;
      
      // スクロール位置が保存されているかチェック（詳細画面からの戻りかどうかの判断材料）
      const savedScrollPosition = localStorage.getItem(SCROLL_POSITION_KEY);
      const isReturningFromDetail = !!savedScrollPosition;
      
      // 詳細画面からの戻りでない場合は復元しない
      if (!isReturningFromDetail) {
        return false;
      }
      
      // 1. フォーム値を復元
      const savedFormValues = localStorage.getItem(SEARCH_FORM_VALUES_KEY);
      if (savedFormValues) {
        try {
          const formValues = JSON.parse(savedFormValues);

          const DATE_FIELDS = ['businesscardcreatetimeFrom', 'businesscardcreatetimeTo'];
          DATE_FIELDS.forEach(field => {
            if (formValues[field]) {
              formValues[field] = dayjs(formValues[field]);
            }
          });

          // フォームにセット
          form.setFieldsValue(formValues);

          const hasValues = Object.values(formValues).some(val =>
            val !== undefined && val !== null && val !== '');

          if (hasValues) {
            hasRestoredConditions = true;
          }
        } catch (error) {
          console.error('フォーム値の解析に失敗:', error);
        }
      }

      // 検索欄の開閉状態は初期値で復元されているため、ここでは処理不要

      // 2. 検索パラメータを復元
      const savedSearchParams = localStorage.getItem(SEARCH_PARAMS_KEY);
      if (savedSearchParams) {
        try {
          const params = JSON.parse(savedSearchParams);

          // 最低限必要なプロパティが含まれているか確認
          if (params && typeof params === 'object' && 'limit' in params) {
            setSearchParams(params);
            
            // optionCustomer から拡張フィールドの値を復元
            if (params.optionCustomer && typeof params.optionCustomer === 'string') {
              const { values, fromValues, toValues } = restoreOptionFieldValuesFromOptionCustomer(params.optionCustomer);
              
              setOptionFieldValues(values);
              setRangeFromValues(fromValues);
              setRangeToValues(toValues);
              
              console.log('拡張フィールド値復元:', values);
              console.log('範囲検索（開始）値復元:', fromValues);
              console.log('範囲検索（終了）値復元:', toValues);
            }
            
            hasRestoredConditions = true;
            // ソート条件復元
            if (params.sortItem) setSortItem(params.sortItem);
            if (params.sortOrder !== undefined) setSortOrder(params.sortOrder);
          } else {
            console.warn('保存された検索パラメータが不完全です:', params);
          }
        } catch (error) {
          console.error('検索パラメータの解析に失敗:', error);
        }
      }

      // 3. 選択された担当者を復元
      const savedContactUsers = localStorage.getItem(SELECTED_CONTACT_USERS_KEY);
      if (savedContactUsers) {
        try {
          const contactUsers = JSON.parse(savedContactUsers);
          if (Array.isArray(contactUsers) && contactUsers.length > 0) {
            setSelectedContactUsers(contactUsers);
            hasRestoredConditions = true;
          }
        } catch (error) {
          console.error('担当者データの解析に失敗:', error);
        }
      }

      return hasRestoredConditions;
    } catch (error) {
      console.error('検索条件の復元に失敗:', error);
      return false;
    }
  };
  

  // 初回レンダリング時に実行する初期化処理
  useEffect(() => {
    
    const defaultParams = buildSearchParams();
    
    // 1. ローカルストレージから検索条件を復元
    const restored = restoreSearchConditions();
    
    // 2. 拡張フィールドを取得（担当者一覧は useContactUsers が取得する）
    fetchOptionFields();
    
    // 3. 検索条件に基づいてデータを取得
    // 遅延実行して、状態更新が完了するのを待つ
    setTimeout(() => {
      // 保存された検索パラメータを取得
      const savedSearchParams = localStorage.getItem(SEARCH_PARAMS_KEY);
      // 保存されたスクロール位置と読み込み済み件数を取得
      const savedScrollPosition = localStorage.getItem(SCROLL_POSITION_KEY);
      const savedLoadedItemsCount = localStorage.getItem(LOADED_ITEMS_COUNT_KEY);
      
      // 初期状態ではデフォルトパラメータを使用
      let paramsToUse = defaultParams;
      
      // 前回の状態を引き継ぐべきかどうかを確認（詳細画面からの戻りなど）
      const shouldRestoreState = savedSearchParams && savedScrollPosition;
      
      if (shouldRestoreState) {
        try {
          // 保存されたパラメータを解析
          const parsedParams = JSON.parse(savedSearchParams);
          
          // 有効なパラメータであることを確認
          if (parsedParams && typeof parsedParams === 'object' && 'limit' in parsedParams) {
            paramsToUse = parsedParams;
            
            // 読み込み済み件数がある場合はそれを使用
            let limitToUse = paramsToUse.limit;
            
            if (savedLoadedItemsCount) {
              const loadedCount = parseInt(savedLoadedItemsCount, 10);
              if (!isNaN(loadedCount) && loadedCount > 0) {
                // 読み込み済み件数以上を取得するため、ページサイズの倍数になるよう切り上げ
                const pageSize = paramsToUse.limit || 20;
                limitToUse = Math.ceil(loadedCount / pageSize) * pageSize;
              }
            }
            
            // 詳細から戻ったときに前と同じ状態を復元するためのパラメータ
            const restoredFetchParams = {
              ...paramsToUse,
              offset: 0, // 常に0から取得
              limit: Math.max(limitToUse, paramsToUse.limit * 3) // 読み込み済み件数か通常の3倍以上
            };
            
            fetchCustomers(restoredFetchParams);
            
            // 現在のページを設定（offsetとlimitから計算）
            if (paramsToUse.offset && paramsToUse.limit) {
              const calculatedPage = Math.floor(paramsToUse.offset / paramsToUse.limit) + 1;
              setCurrentPage(calculatedPage);
            }
            
            // スクロール位置を復元（データ読み込み後に実行）
            const scrollY = parseInt(savedScrollPosition, 10);
            if (!isNaN(scrollY) && scrollY > 0) {
              // データが読み込まれた後に遅延実行
              setTimeout(() => {
                window.scrollTo(0, scrollY);
              }, 500);
            }
          } else {
            console.warn('保存された検索パラメータが不完全です。デフォルトパラメータを使用します。');
            fetchCustomers(defaultParams);
          }
        } catch (error) {
          console.error('検索パラメータのパースに失敗:', error);
          fetchCustomers(defaultParams);
        }
      } else {
        fetchCustomers(defaultParams);
      }

      // 初期化完了後、以降は検索欄の状態を保存できるようにする
      setTimeout(() => {
        isInitialMount.current = false;
      }, 500);
    }, 300);

  }, []); // 依存配列を空にして、コンポーネントのマウント時に一度だけ実行

  // Flutterからのフッター高さ通知を受け取る
  useEffect(() => {
    const handleFlutterFooterStateChange = (event: any) => {
      const footerHeight = event.detail?.footerHeight || 0;
      console.log('Flutter footer height received:', footerHeight);
      setFlutterFooterHeight(footerHeight);
    };

    // 初期値をチェック（Flutterから既に設定されている可能性がある）
    if (window.flutterFooterState?.footerHeight) {
      setFlutterFooterHeight(window.flutterFooterState.footerHeight);
    }

    // イベントリスナーを追加
    window.addEventListener('flutterFooterStateChange', handleFlutterFooterStateChange);

    // クリーンアップ関数
    return () => {
      window.removeEventListener('flutterFooterStateChange', handleFlutterFooterStateChange);
    };
  }, []);

  // customers の変更を監視して、初回データ取得後に自動的に画像を読み込む
  useEffect(() => {
    // 初回データ取得または検索による更新時に実行
    if (customers.length > 0) {
      
      // スクロール位置の復元を再試行（データ読み込み完了後）
      const savedScrollPosition = localStorage.getItem(SCROLL_POSITION_KEY);
      if (savedScrollPosition) {
        const scrollY = parseInt(savedScrollPosition, 10);
        if (!isNaN(scrollY) && scrollY > 0) {
          // データが読み込まれた後に遅延実行
          setTimeout(() => {
            window.scrollTo(0, scrollY);
            localStorage.removeItem(SCROLL_POSITION_KEY);
          }, 200);
        }
      }
      
      // 画像取得処理
      if (showCardImages && !loadingImages) {
        
        // すぐに実行すると状態更新のタイミングで問題が生じる可能性があるため、少し遅延させる
        setTimeout(() => {
          fetchCustomerImages(customers);
        }, 300);
      }
    }
  }, [customers.length, showCardImages]);
  
  // スクロールイベントの監視
  useEffect(() => {
    const handleScroll = () => {
      if (!listRef.current) return;
      
      // スクロール位置がリストの底部に近づいたら追加データを読み込む
      const { scrollTop, clientHeight, scrollHeight } = document.documentElement;
      if (scrollTop + clientHeight >= scrollHeight - 300 && !isLoading && hasMore) {
        loadMoreData();
      }
    };
    
    // スクロールイベントリスナーを追加
    window.addEventListener('scroll', handleScroll);
    
    // クリーンアップ関数
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [loadMoreData, isLoading, hasMore]);

  

  // 検索実行
  const handleSearch = async () => {
    const values = await form.validateFields();

    // 手動でフィールドをクリアした場合に前回条件が残らないよう、
    // 前回 searchParams を引き継がずベース値だけから組み立てる
    const newParams: CustomerSearchRequest = {
      limit: pageSize,
      offset: 0,
      usecustomernamefurigana: true,
      usecompanynamefurigana: true,
      sortItem,
      sortOrder,
    };

    // 顧客名の検索キーワードがある場合
    if (values.customerName) {
      newParams.customername = values.customerName.trim();
    }

    // メールアドレスの検索キーワードがある場合
    if (values.mailAddress) {
      newParams.mailaddress = values.mailAddress.trim();
    }

    // 会社名
    if (values.company) {
      newParams.companyname = values.company.trim();
    }

    // 住所
    if (values.address) {
      newParams.address = values.address.trim();
    }

    // 備考
    if (values.remarks) {
      newParams.remarks = values.remarks.trim();
    }

    // 電話番号/携帯電話番号
    if (values.phone) {
      newParams.telMobiletel = values.phone.trim();
    }

    // ランク
    if (values.rank) {
      newParams.rankno = parseInt(values.rank);
    }

    // 担当者
    if (selectedContactUsers.length > 0) {
      newParams.contactusernoList = selectedContactUsers;
    }

    // 名刺登録日 From（例：2026/1/1→20260101000000）
    if (values.businesscardcreatetimeFrom) {
      newParams.businesscardcreatetimeFrom =
        values.businesscardcreatetimeFrom.format('YYYYMMDD') + '000000';
    }

    // 名刺登録日 To（例：2026/12/31→20261231235959）
    if (values.businesscardcreatetimeTo) {
      newParams.businesscardcreatetimeTo =
        values.businesscardcreatetimeTo.format('YYYYMMDD') + '235959';
    }

    // 拡張フィールドの検索条件を追加
    if (Object.keys(optionFieldValues).length > 0 ||
        Object.keys(rangeFromValues).length > 0 ||
        Object.keys(rangeToValues).length > 0) {

      // API形式に変換
      const optionCustomer = convertOptionFieldValuesToApiFormat(
        optionFieldValues,
        rangeFromValues,
        rangeToValues,
        optionFields
      );

      if (optionCustomer) {
        newParams.optionCustomer = optionCustomer;
        console.log('拡張フィールド検索条件:', optionCustomer);
      }
    }

    // レイジーロード関連の状態をリセット
    setHasMore(true);
    setCustomers([]);
    setCurrentPage(1);
    
    // ログ出力
    
    // 検索実行
    setSearchParams(newParams);
    fetchCustomers(newParams);
    
    // 検索条件を明示的に保存（フォーム値も含めて）
    // 少し遅延させて、非同期の状態更新が完了するのを待つ
    setTimeout(() => {
      saveSearchConditions(newParams);
    }, 300);
  };

  // ソートの条件変更関数
  const handleSortChange = (
    newSortItem: SortItemValue,
    newSortOrder: boolean
  ) => {
    setSortItem(newSortItem);
    setSortOrder(newSortOrder);

    const newParams = buildSearchParams({
      sortItem: newSortItem,
      sortOrder: newSortOrder,
    });

    setHasMore(true);
    setCustomers([]);
    setCurrentPage(1);
    setSearchParams(newParams);
    fetchCustomers(newParams);
    saveSearchConditions(newParams);
  };

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Header style={{ 
        background: '#fff', 
        padding: '0 16px', 
        display: 'flex', 
        alignItems: 'center',
        height: 'auto',
        minHeight: '64px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}>
        <Button 
          shape="circle"
          size="small"
          icon={<ArrowLeftOutlined />} 
          onClick={() => {
            // メニュー画面に戻る前に検索条件のローカルストレージを破棄
            localStorage.removeItem(SEARCH_FORM_VALUES_KEY);
            localStorage.removeItem(SEARCH_PARAMS_KEY);
            localStorage.removeItem(SELECTED_CONTACT_USERS_KEY);
            localStorage.removeItem(SCROLL_POSITION_KEY);
            localStorage.removeItem(LOADED_ITEMS_COUNT_KEY);
            
            // メニュー画面に遷移
            navigate(getRoutePath('/menu'));
          }}
          style={{
            backgroundColor: 'white',
            borderColor: '#d9d9d9',
            borderWidth: '2px',
            color: '#595959',
            marginRight: '8px',
            width: '32px',
            height: '32px',
            minWidth: '32px',
            minHeight: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}
        />
        <div style={{ flex: 1, textAlign: 'center' }}>
          登録済み名刺一覧
        </div>
        {!isInFlutter && canRegisterCustomer && (
          <Button
            type="primary"
            icon={<UserOutlined />}
            onClick={() => navigate(getRoutePath('/camera'))}
            style={{ marginLeft: '8px' }}
          >
            名刺取込
          </Button>
        )}
      </Header>
      
      <Content style={{
        padding: '16px',
        paddingBottom: `${16 + flutterFooterHeight}px`,
        background: '#f0f2f5'
      }}>
        {/* 検索エリア */}
        <div style={{ 
          background: '#fff', 
          borderRadius: '4px', 
          marginBottom: '16px', 
          overflow: 'hidden',
          transition: 'all 0.3s',
          boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
        }}>
          <div 
            style={{ 
              padding: '10px 16px', 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              borderBottom: searchExpanded ? '1px solid #f0f0f0' : 'none',
              cursor: 'pointer',
              background: '#f5faff'
            }}
            onClick={() => setSearchExpanded(!searchExpanded)}
          >
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <SearchOutlined style={{ marginRight: '8px', color: '#0693e3' }} />
              <Text strong>検索</Text>
            </div>
            <Button 
              type="text" 
              icon={searchExpanded ? <UpOutlined /> : <DownOutlined />}
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setSearchExpanded(!searchExpanded);
              }}
            />
          </div>
          
          {searchExpanded && (
            <div style={{ padding: '16px' }}>
              <Form form={form} layout="vertical">
                <Row gutter={16}>
                  <Col xs={24} sm={12}>
                    <Form.Item label="会社名" name="company">
                      <Input placeholder="会社名で検索" prefix={<BankOutlined style={{ color: '#bfbfbf' }} />} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12} md={6}>
                    <Form.Item label="顧客名" name="customerName">
                      <Input placeholder="顧客名で検索" prefix={<UserOutlined style={{ color: '#bfbfbf' }} />} />
                    </Form.Item>
                  </Col>
                </Row>
                
                <div 
                  style={{ 
                    marginTop: '8px', 
                    marginBottom: '16px', 
                    display: 'flex', 
                    alignItems: 'center',
                    cursor: 'pointer'
                  }}
                  onClick={() => setAdvancedSearchExpanded(!advancedSearchExpanded)}
                >
                  <FilterOutlined style={{ marginRight: '8px', color: '#0693e3' }} />
                  <Text type="secondary">詳細検索</Text>
                  {advancedSearchExpanded ? <UpOutlined style={{ marginLeft: '8px', fontSize: '12px' }} /> : <DownOutlined style={{ marginLeft: '8px', fontSize: '12px' }} />}
                </div>
                
                {advancedSearchExpanded && (
                  <>
                    <Row gutter={16}>
                      <Col xs={24} sm={12} md={8}>
                        <Form.Item label="ランク" name="rank">
                          <Select 
                            placeholder="ランクを選択" 
                            allowClear
                            loading={isLoadingRanks}
                            options={rankOptions}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12} md={16}>
                        <Form.Item label="担当者" name="contactUsers">
                          {renderContactUsersSelector()}
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row gutter={16}>
                      <Col xs={24} sm={12} md={8}>
                        <Form.Item label="TEL/携帯" name="phone">
                          <Input placeholder="電話番号" />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12} md={8}>
                        <Form.Item label="住所" name="address">
                          <Input placeholder="住所" />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12} md={8}>
                        <Form.Item label="E-mail" name="mailAddress">
                          <Input placeholder="メールアドレスで検索" prefix={<MailOutlined style={{ color: '#bfbfbf' }} />} />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row gutter={16}>
                      <Col xs={24}>
                        <Form.Item label="備考" name="remarks">
                          <Input.TextArea placeholder="備考に含まれる文字列" rows={2} />
                        </Form.Item>
                      </Col>
                    </Row>
                     {/* 名刺登録日 */}
                    <Row gutter={16}>
                      <Col xs={24} sm={12}>
                        <Form.Item label="名刺登録日">
                          <Space direction="vertical" style={{ width: '100%' }} size="small">
                          {/* 開始日 */}
                          <Space.Compact style={{ width: '100%' }}>
                            <Form.Item name="businesscardcreatetimeFrom" noStyle>
                              <DatePicker
                                style={{ flex: 1 }}
                                placeholder="開始日"
                                format="YYYY/MM/DD"
                              />
                            </Form.Item>
                            <Button
                              onClick={() => form.setFieldValue('businesscardcreatetimeFrom', null)}
                              icon={<CloseCircleFilled style={{ color: '#bfbfbf' }} />}
                              style={{ flexShrink: 0, padding: '0 4px', border: '1px solid #e0e0e0', boxShadow: 'none', background: 'transparent' }}
                              title="クリア"
                            />
                          </Space.Compact>
                          {/* 終了日 */}
                          <Space.Compact style={{ width: '100%' }}>
                            <Form.Item name="businesscardcreatetimeTo" noStyle>
                              <DatePicker
                                style={{ flex: 1 }}
                                placeholder="終了日"
                                format="YYYY/MM/DD"
                              />
                            </Form.Item>
                            <Button
                              onClick={() => form.setFieldValue('businesscardcreatetimeTo', null)}
                              icon={<CloseCircleFilled style={{ color: '#bfbfbf' }} />}
                              style={{ flexShrink: 0, padding: '0 4px', border: '1px solid #e0e0e0', boxShadow: 'none', background: 'transparent' }}
                              title="クリア"
                            />
                          </Space.Compact>
                        </Space>
                        </Form.Item>

                      </Col>
                    </Row>
                    {/* 拡張フィールド */}
                    {loadingOptionFields ? (
                      <div style={{ textAlign: 'center', padding: '20px' }}>
                        <Spin size="small" />
                        <div style={{ marginTop: '8px', fontSize: '12px', color: '#8c8c8c' }}>
                          拡張フィールドを読み込み中...
                        </div>
                      </div>
                    ) : (
                      optionFields?.customeroptionfieldlist &&
                      optionFields.customeroptionfieldlist.length > 0 && (
                        <div style={{ marginTop: '16px' }}>
                          <div style={{
                            marginBottom: '12px',
                            paddingBottom: '8px',
                            borderBottom: '1px solid #e8f4f8'
                          }}>
                            <Text strong style={{ color: '#0693e3', fontSize: '14px' }}>
                              顧客追加項目
                            </Text>
                          </div>
                          <OptionFieldGroup
                            title=""
                            fields={optionFields.customeroptionfieldlist}
                            values={optionFieldValues}
                            rangeFromValues={rangeFromValues}
                            rangeToValues={rangeToValues}
                            onFieldChange={handleOptionFieldChange}
                            onRangeFromChange={handleRangeFromChange}
                            onRangeToChange={handleRangeToChange}
                            prefix="customer"
                            readonly={false}
                            kind="search" // 検索画面なのでデフォルト値は使わない
                            bordered={false}
                          />
                        </div>
                      )
                    )}
                  </>
                )}
                
                <Row justify="end" gutter={16} style={{ marginTop: '16px' }}>
                  <Col>
                    <Button onClick={() => {
                      // unmount 中に setFieldsValue で書き込まれた値が resetFields で消えないケースに備えて
                      // 各フィールドを明示的に undefined/null にしてから resetFields する
                      form.setFieldsValue({
                        company: undefined,
                        customerName: undefined,
                        mailAddress: undefined,
                        address: undefined,
                        remarks: undefined,
                        phone: undefined,
                        rank: undefined,
                        businesscardcreatetimeFrom: null,
                        businesscardcreatetimeTo: null,
                      });
                      form.resetFields();
                      // 選択された担当者をクリア
                      setSelectedContactUsers([]);
                      // 拡張フィールドの値をクリア
                      setOptionFieldValues({});
                      setRangeFromValues({});
                      setRangeToValues({});
                      // searchParams state を初期値に戻す
                      // （buildSearchParams が ...searchParams で前回条件を引き継いでしまうため）
                      setSearchParams({
                        limit: pageSize,
                        offset: 0,
                        usecustomernamefurigana: true,
                        usecompanynamefurigana: true,
                      });
                      // 保存された検索条件を削除 (ローカルストレージを使用)
                      localStorage.removeItem(SEARCH_FORM_VALUES_KEY);
                      localStorage.removeItem(SEARCH_PARAMS_KEY);
                      localStorage.removeItem(SELECTED_CONTACT_USERS_KEY);
                      // スクロール位置と読み込み済み件数もクリア
                      localStorage.removeItem(SCROLL_POSITION_KEY);
                      localStorage.removeItem(LOADED_ITEMS_COUNT_KEY);
                    }}>
                      クリア
                    </Button>
                  </Col>
                  <Col>
                    <Button 
                      type="primary" 
                      icon={<SearchOutlined />}
                      onClick={handleSearch}
                      loading={isLoading}
                    >
                      検索
                    </Button>
                  </Col>
                </Row>
              </Form>
            </div>
          )}
        </div>
        
        {/* 件数とオプション表示エリア */}
        <div style={{ marginBottom: '16px' }}>
          {/* 件数 + ソート 表示 */}
          <div style={{
            padding: '10px 16px',
            background: '#f0f7ff',
            borderRadius: '4px',
            borderLeft: '3px solid #0693e3',
            borderRight: '1px solid #e8e8e8',
            borderTop: '1px solid #e8e8e8',
            borderBottom: '1px solid #e8e8e8',
            boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
          }}>
            {/* 件数 + 名刺画像表示切替 */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '8px'
            }}>
              <Text>
                全 <strong>{totalCount}</strong> 件
                {isLoading && <LoadingOutlined style={{ marginLeft: 8 }} />}
              </Text>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <PictureOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                <Text style={{ marginRight: '8px', fontSize: '14px' }}>名刺画像表示:</Text>
                <Switch
                  checkedChildren={<EyeOutlined />}
                  unCheckedChildren={<EyeInvisibleOutlined />}
                  checked={showCardImages}
                  onChange={(checked) => setShowCardImages(checked)}
                  size="small"
                />
              </div>
            </div>
            {/* ソート：仕切り線 + 1行 */}
            <div style={{
              borderTop: '1px solid #ddeeff',
              paddingTop: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: '0',
            }}>
              {/* 項目ボタン（選択中は矢印付き、再タップで昇降反転） */}
              <div style={{ display: 'flex', gap: '0', flex: 1 }}>
                {SORT_OPTIONS.map((option) => {
                  const isActive = sortItem === option.value;
                  return (
                    <button
                      key={option.value}
                      className={`sort-button${isActive ? ' active' : ''}`}
                      onClick={() =>
                        isActive
                          ? handleSortChange(option.value, !sortOrder)
                          : handleSortChange(option.value, sortOrder)
                      }
                    >
                      {option.label}{isActive ? (sortOrder ? ' ▲' : ' ▼') : ''}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
        
        {customers.length === 0 && isLoading ? (
          <div style={{ 
            textAlign: 'center', 
            padding: '32px', 
            background: '#fff', 
            borderRadius: '4px',
            marginTop: '16px'
          }}>
            <LoadingOutlined style={{ fontSize: 24 }} /> 
            <div style={{ marginTop: 16 }}>データを読み込み中...</div>
          </div>
        ) : customers.length > 0 ? (
          <div ref={listRef}>
            <List
              itemLayout="horizontal"
              dataSource={customers}
            renderItem={(item) => {
              // デバッグ用：レンダリング時の各アイテムの内容を確認
              const customerNo = item.customerno || item.no;
              const createtime = customerImages[customerNo]?.createtime;
              const formattedDate = createtime ? new Date(createtime).toLocaleDateString('ja-JP') : null;

              return (
                <List.Item 
                  style={{ 
                    padding: '12px 16px', 
                    marginBottom: '16px', 
                    background: '#fff', 
                    borderRadius: '8px',
                    cursor: 'pointer',
                    boxShadow: '0 2px 6px rgba(0,0,0,0.05)'
                  }}
                  onClick={() => {
                    // 詳細画面へ遷移する前に現在の検索条件を明示的に保存
                    
                    // 現在のスクロール位置を保存
                    const scrollPosition = window.scrollY;
                    localStorage.setItem(SCROLL_POSITION_KEY, scrollPosition.toString());
                    
                    // 読み込み済みの件数を保存
                    localStorage.setItem(LOADED_ITEMS_COUNT_KEY, customers.length.toString());

                    // 詳細画面へ遷移する前にランク情報を保存
                    const customerId = item.customerno || item.no;
                    const rankName = item.rankname || '';
                    // 顧客IDとランク名を関連付けて保存
                    // 選択されたランクのインデックスを取得
                    // item.ranknoはnumberだが、rankOptions.valueはstringなので、文字列に変換して比較
                    // nullやundefinedの場合も考慮
                    const rankValue = (item.rankno !== undefined && item.rankno !== null) ? item.rankno.toString() : '';
                    const rankIndex = rankOptions.findIndex(opt => opt.value === rankValue);
                    
                    // ランク色を取得
                    const rankColor = getRankColorSync(rankName, rankColors);
                    
                    // ランク情報をインデックスと色も含めて保存
                    saveCustomerRank(customerId, rankName, rankIndex, rankColor)
                      .then(() => {
                      })
                      .catch(error => {
                        console.error(`顧客ID ${customerId} のランク情報保存エラー:`, error);
                      });
                    
                    // 詳細画面へ遷移（IDはURLパラメータではなくIndexedDBに保存）
                    saveCurrentDetailCustomerId(customerId)
                      .then(() => {
                        // URLパラメータなしで詳細画面へ遷移
                        navigate(getRoutePath('/business-card-detail'));
                      })
                      .catch(error => {
                        console.error('詳細画面遷移時のエラー:', error);
                        message.error('詳細画面への遷移に失敗しました');
                      });
                  }}
              >
                <div style={{ width: '100%' }}>
                  <div style={{ 
                    display: 'flex',
                    justifyContent: 'space-between', 
                    width: '100%'
                  }}>
                    {/* 名刺情報部分（上下レイアウト）*/}
                    <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                      {/* 名刺画像表示（上部） */}
                      {showCardImages ? (
                        <div style={{ marginBottom: '12px', width: '100%' }}>
                          {/* 名刺画像 */}
                          <div style={{
                            width: '100%',
                            height: '120px',
                            overflow: 'hidden',
                            borderRadius: formattedDate ? '4px 4px 0 0' : '4px',
                            border: '1px solid #eee',
                            borderBottom: formattedDate ? 'none' : '1px solid #eee',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: '#f5f5f5'
                          }}>
                            {/* 画像が読み込まれている場合 */}
                            {customerImages[customerNo]?.front ? (
                              <img
                                src={customerImages[customerNo].front}
                                alt="名刺"
                                style={{ 
                                width: '100%', 
                                height: '100%', 
                                objectFit: 'contain' 
                              }}
                              onError={(e) => {
                                // 画像読み込みエラー時の処理
                                console.error('名刺画像読み込みエラー:', e);
                                (e.target as HTMLImageElement).style.display = 'none';
                              }}
                              />
                            ) : (
                              // 画像が読み込まれていない場合はローディングまたはプレースホルダー
                              <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '100%',
                                height: '100%'
                              }}>
                                {loadingImages ? (
                                  <Spin size="small" />
                                ) : (
                                  <>
                                    <FileTextOutlined style={{ fontSize: '24px', color: '#d9d9d9' }} />
                                    <div style={{ fontSize: '12px', color: '#d9d9d9', marginTop: '4px' }}>No Image</div>
                                  </>
                                )}
                              </div>
                            )}
                          </div>

                          {/* 名刺登録日（日付がある場合のみ表示） */}
                          {formattedDate && (
                            <div style={{ marginTop: '4px', paddingLeft: '2px' }}>
                              <span style={{ fontSize: '11px', color: '#aaa' }}>名刺登録日: {formattedDate}</span>
                            </div>
                          )}

                        </div>
                      ) : null}
                      
                      {/* 名刺情報（下部） */}
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
                          <Tag 
                            color={(() => {
                              // ranknoの値を直接インデックスとして使用
                              if (item.rankno !== undefined && item.rankno !== null && item.rankno >= 0) {
                                // ranknoに基づいて色を取得（インデックスは0から始まるため調整が必要な場合あり）
                                return getRankColorByIndex(item.rankno);
                              } else if (item.rankname) {
                                // ranknoがない場合はrankColorsマッピングから色を取得
                                return rankColors[item.rankname] || '#bfbfbf';
                              }
                              return '#bfbfbf'; // デフォルト色
                            })()} 
                            style={{ 
                              minWidth: '20px',
                              maxWidth: '70px',
                              height: 'auto',
                              lineHeight: '18px', 
                              textAlign: 'center', 
                              padding: '0 8px', 
                              fontSize: '12px', 
                              fontWeight: 'bold',
                              marginRight: '8px',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}
                          >
                            {item.rankname || ''}
                          </Tag>
                          <div style={{ fontWeight: 'bold', fontSize: '16px' }}>
                            {item.customername}
                          </div>
                        </div>
                        
                        <div>
                          <Text style={{ fontSize: '14px' }} ellipsis>
                            {item.companyname}{item.deptname ? ` / ${item.deptname}` : ''}
                          </Text>
                          {item.position && (
                            <Text style={{ fontSize: '13px', color: '#8c8c8c', marginLeft: '8px' }}>
                              {item.position}
                            </Text>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* 詳細表示ボタン */}
                    <div style={{ display: 'flex', alignItems: 'center', paddingLeft: '8px' }}>
                      <Button 
                        type="text"
                        icon={expandedItems.has(item.customerno || item.no) ? <UpOutlined /> : <DownOutlined />}
                        onClick={(e) => {
                          e.stopPropagation();
                          setExpandedItems(prev => {
                            const newSet = new Set(prev);
                            const itemId = item.customerno || item.no;
                            if (newSet.has(itemId)) {
                              newSet.delete(itemId);
                            } else {
                              newSet.add(itemId);
                            }
                            return newSet;
                          });
                        }}
                        style={{ padding: '4px 8px' }}
                      />
                    </div>
                  </div>
                  
                  {expandedItems.has(item.customerno || item.no) && (
                    <div 
                      style={{ 
                        padding: '12px 12px',
                        marginTop: '8px',
                        backgroundColor: '#f5f9fd',
                        borderRadius: '4px',
                        borderTop: '1px dashed #e8e8e8'
                      }}
                    >
                      <Space direction="vertical" size={12} style={{ width: '100%' }}>
                        {/* 名刺画像の表示は削除 */}
                        
                        <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                          <MailOutlined style={{ marginRight: '8px', marginTop: '4px', color: '#0693e3' }} />
                          <div style={{ flex: 1 }}>
                            <div style={{ 
                              fontSize: '13px', 
                              color: '#0693e3', 
                              marginBottom: '4px',
                              fontWeight: 'bold'
                            }}>E-mail</div>
                            <div style={{ fontSize: '14px' }}>{item.mailaddress || '未登録'}</div>
                          </div>
                        </div>
                        
                        <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                          <HomeOutlined style={{ marginRight: '8px', marginTop: '4px', color: '#0693e3' }} />
                          <div style={{ flex: 1 }}>
                            <div style={{ 
                              fontSize: '13px', 
                              color: '#0693e3', 
                              marginBottom: '4px',
                              fontWeight: 'bold'
                            }}>住所</div>
                            <div style={{ fontSize: '14px' }}>{item.address || '未登録'}</div>
                          </div>
                        </div>
                        
                        <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                          <FileTextOutlined style={{ marginRight: '8px', marginTop: '4px', color: '#0693e3' }} />
                          <div style={{ flex: 1 }}>
                            <div style={{ 
                              fontSize: '13px', 
                              color: '#0693e3', 
                              marginBottom: '4px',
                              fontWeight: 'bold'
                            }}>備考</div>
                            <div style={{ fontSize: '14px', whiteSpace: 'pre-wrap' }}>{item.remarks || '未登録'}</div>
                          </div>
                        </div>
                      </Space>
                    </div>
                  )}
                </div>
                </List.Item>
              );
            }}
            />
            {/* レイジーロード用のローディングインジケーター */}
            {hasMore && (
              <div style={{ textAlign: 'center', margin: '20px 0' }}>
                <Spin tip="読み込み中..." />
              </div>
            )}
            
            {/* デバッグ用：現在のデータ状態 */}
            {process.env.NODE_ENV === 'development' && (
              <div style={{ marginTop: '20px', padding: '10px', border: '1px dashed #ccc', borderRadius: '4px', background: '#f9f9f9' }}>
                <h4>デバッグ情報</h4>
                <p>読込件数: {customers.length}件</p>
                <p>画像表示設定: {showCardImages ? 'ON' : 'OFF'}</p>
                <p>画像取得状態: {loadingImages ? '取得中...' : '待機中'}</p>
                <p>画像データ保持件数: {Object.keys(customerImages).length}件</p>
                <details>
                  <summary>保持している画像データのID一覧</summary>
                  <ul style={{ maxHeight: '100px', overflow: 'auto', fontSize: '12px' }}>
                    {Object.keys(customerImages).map(id => (
                      <li key={id}>ID: {id} (表面: {customerImages[Number(id)].front ? 'あり' : 'なし'}, 裏面: {customerImages[Number(id)].back ? 'あり' : 'なし'})</li>
                    ))}
                  </ul>
                </details>
              </div>
            )}
          </div>
        ) : (
          <div style={{ 
            textAlign: 'center', 
            padding: '32px', 
            background: '#fff', 
            borderRadius: '4px',
            marginTop: '16px'
          }}>
            <Text>登録済みの名刺がありません</Text>
          </div>
        )}
      </Content>
    </Layout>
  );
};

export default BusinessCardListPage;